import { useQuery } from "@tanstack/react-query";
import { ColumnFiltersState } from "@tanstack/table-core";
import { useMemo } from "react";

import { QueryHandHistoryErrorsRequestDto } from "@/shared/api";
import { useApi } from "@/shared/api/use-api";

type UseQueryHandHistoryErrorsParams = {
  pageNumber: number;
  pageSize: number;
  columnFilters: ColumnFiltersState;
  orderAsc: boolean;
};

const splitFilterValue = (value: unknown): string[] =>
  String(value ?? "")
    .split(",")
    .map((it) => it.trim())
    .filter((it) => it.length > 0);

export const useQueryHandHistoryErrors = ({
  pageNumber,
  pageSize,
  columnFilters,
  orderAsc,
}: UseQueryHandHistoryErrorsParams) => {
  const { statAnalyticsApi } = useApi();

  const request = useMemo<QueryHandHistoryErrorsRequestDto>(() => {
    const filterValue = (id: string) => columnFilters.find((it) => it.id === id)?.value;

    return {
      room_ids: splitFilterValue(filterValue("room_id")),
      game_formats: splitFilterValue(filterValue("game_format")),
      game_types: splitFilterValue(filterValue("game_type")),
      processors: splitFilterValue(filterValue("processor")),
      game_ids: splitFilterValue(filterValue("game_id")),
      trace_ids: splitFilterValue(filterValue("trace_id")),
      error_message_contains: String(filterValue("error_message") ?? ""),
      include_data: true,
      order_asc: orderAsc,
      page: {
        page_number: pageNumber,
        page_size: pageSize,
      },
    };
  }, [columnFilters, orderAsc, pageNumber, pageSize]);

  return useQuery({
    queryKey: ["/statistic-analytic/analytics/hand-history-errors", request],
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: ({ signal }) => statAnalyticsApi.queryHandHistoryErrors(request, { signal }),
  });
};
